// @ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import usePersistedState from '../utils/mapPersistedState';
import { apiRequest } from "../../services/utils/HttpUtils";
import { useQuery } from "@tanstack/react-query";
import { getUserInfo } from "../../graphql/queries_user_info";
import { Oval } from 'react-loader-spinner';
import {
    Stack,
    FormControl,
    Grid,
    MenuItem,
    InputLabel,
    Select,
    Box
} from "@mui/material";
import {
    Chart as ChartJS,
    TimeScale
} from "chart.js";
import Annotation from 'chartjs-plugin-annotation';
import 'chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm';
import { Chart } from "react-chartjs-2";
import hexToRgb from "./colours";
import { RetrieveLabourSupplyDemand, RetrieveLabourOccupation } from "../../graphql/queries_nationalMap";
import MapPlotFilterList from "../utils/mapPlotFilterList";
import {
    labourShortageFiltersConfigList
} from "./nationalMapFiltersConfig";
import { labourCaveat } from "../../assets/texts/pageTextContent";

ChartJS.register(
    TimeScale,
    Annotation
);

export default function LabourShortageChart({ queryVariables, allMapLists, setLabourPlotQueryProps, labourPlotQueryProps, applyUpdates, setLabourTab, updateLabourCharts, updateAlert }) {

    // Authentication
    const { instance, accounts } = useMsal();
    const { data: userInfo } = getUserInfo();

    const labourChartLoader = useRef();

    const labour_colours = [{ "labour": "demand", "colour": "#253746" },
    { "labour": "supply", "colour": "#73b469" },
    { "labour": "difference", "colour": "#7e2d40" }];

    const labourSupplyDemandValuesRef = useRef<any[]>([]);
    const labourOccupationValuesRef = useRef<any[]>([]);

    const [completed, setCompleted] = useState(0);
    const [month, setMonth] = usePersistedState<any>('month', {
        inMonth: queryVariables.inMonth,
        inYear: queryVariables.inYear
    });

    const { data: labourSupplyDemandValues, status: labourSupplyDemandStatus, fetchStatus: labourSupplyDemandFetchStatus, error: labourSupplyDemandError, refetch: refetchLabourSupplyDemandValues } = useQuery({
        queryKey: ["LabourSupplyDemandValues"],
        queryFn: () => {
            return apiRequest(
                {
                    query: RetrieveLabourSupplyDemand,
                    variables: queryVariables,
                    instance: instance,
                    accounts: accounts
                })
        },
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        cacheTime: Infinity,
        enabled: !!userInfo
    });

    const { data: labourOccuptionValues, status: labourOccupationStatus, fetchStatus: labourOccupationFetchStatus, error: labourOccupationError, refetch: refetchLabourOccupationValues } = useQuery({
        queryKey: ["LabourOccupationValues"],
        queryFn: () => {
            return apiRequest(
                {
                    query: RetrieveLabourOccupation,
                    variables: queryVariables,
                    instance: instance,
                    accounts: accounts
                })
        },
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        cacheTime: Infinity,
        enabled: !!userInfo
    });

    // Start - labour demand/supply chart
    // Set the labour demand/supply chart options
    const labour_monthly_options = {
        plugins: {
            title: {
                display: false,
                text: 'Supply vs demand over time (FTE per month)'
            },
            tooltip: {
                callbacks: {
                    labelColor: (tooltipItem) => {
                        return {
                            borderColor: tooltipItem.dataset.borderColor,
                            backgroundColor: tooltipItem.dataset.backgroundColor
                        }
                    }
                }
            },
            legend: {
                position: "bottom",
                onClick: null
            },
            annotation: {
                drawTime: 'afterDatasetsDraw',
                annotations: [{
                    display: (context, opts) => {
                        const date = new Date(new Date(`${month.inYear}-${month.inMonth}-01`).toDateString());
                        const startFiscalDate = new Date(new Date(`${queryVariables.inStartYear - 1}-07-01`).toDateString());
                        const endFiscalDate = new Date(new Date(`${queryVariables.inEndYear}-06-30`).toDateString());
                        return ((queryVariables.inStartYear === 0 || date >= startFiscalDate) && (queryVariables.inEndYear === 0 || date <= endFiscalDate)) ? true : false;
                    },
                    scaleID: 'x',
                    mode: 'vertical',
                    type: 'line',
                    value: `${month.inYear}-${month.inMonth >= 10 ? month.inMonth : '0' + month.inMonth}-01T00:00:00.000Z`,
                    borderColor: '#555555',
                    borderDash: [5, 5],
                    borderWidth: 2,
                    label: {
                        display: true,
                        backgroundColor: 'rgba(51, 51, 51, 0.6)',
                        position: 'end',
                        content: new Date(`${month.inYear}-${month.inMonth}`).toLocaleDateString("en-AU", { year: "numeric", month: "long" })
                    }
                }]
            }
        },
        interaction: {
            mode: 'index',
            intersect: false
        },
        responsive: false,
        aspectRatio: 1,
        scales: {
            x: {
                type: 'time',
                time: {
                    unit: 'month',
                    tooltipFormat: 'MMMM, YYYY'
                }
            }
        }
    };

    // Build the chart datasets dynamically
    const labour_value_datasets = [
        {
            type: 'line',
            label: 'Demand',
            data: labourSupplyDemandValuesRef.current.map((obj) => obj.Demand),
            backgroundColor: labour_colours.find(x => x.labour === 'demand')?.colour,
            borderColor: labour_colours.find(x => x.labour === 'demand')?.colour,
            pointBorderColor: 'rgba(0, 0, 0, 0)',
            pointBackgroundColor: 'rgba(0, 0, 0, 0)',
            pointHoverBackgroundColor: labour_colours.find(x => x.labour === 'demand')?.colour,
            pointHoverBorderColor: labour_colours.find(x => x.labour === 'demand')?.colour
        },
        {
            type: 'line',
            label: 'Supply',
            data: labourSupplyDemandValuesRef.current.map((obj) => obj.Supply),
            backgroundColor: labour_colours.find(x => x.labour === 'supply')?.colour,
            borderColor: labour_colours.find(x => x.labour === 'supply')?.colour,
            pointBorderColor: 'rgba(0, 0, 0, 0)',
            pointBackgroundColor: 'rgba(0, 0, 0, 0)',
            pointHoverBackgroundColor: labour_colours.find(x => x.labour === 'supply')?.colour,
            pointHoverBorderColor: labour_colours.find(x => x.labour === 'supply')?.colour
        },
        {
            type: 'line',
            label: 'Net Difference',
            data: labourSupplyDemandValuesRef.current.map((obj) => obj.Difference),
            fill: true,
            backgroundColor: hexToRgb(labour_colours.find(x => x.labour === 'difference')?.colour, 0.5),
            borderColor: labour_colours.find(x => x.labour === 'difference')?.colour,
            pointBorderColor: 'rgba(0, 0, 0, 0)',
            pointBackgroundColor: 'rgba(0, 0, 0, 0)',
            pointHoverBackgroundColor: labour_colours.find(x => x.labour === 'difference')?.colour,
            pointHoverBorderColor: labour_colours.find(x => x.labour === 'difference')?.colour
        }
    ];

    // Create the unique dates array
    const dates = [...new Set(labourSupplyDemandValuesRef.current.map(item => item.ObservationDate))];
    const labour_value_chart = {
        labels: dates,
        datasets: labour_value_datasets
    };
    // End - labour demand/supply chart

    // Start - labour occupation chart
    const labour_occupation_options = {
        indexAxis: 'y',
        plugins: {
            title: {
                display: false,
                text: 'Supply vs demand by occupation (FTE per month)'
            },
            tooltip: {
                callbacks: {
                    label: (tooltipItem) => {
                        if (tooltipItem.datasetIndex === 0) {
                            return 'Demand: ' + tooltipItem.parsed.x;
                        } else if (tooltipItem.datasetIndex === 1) {
                            return 'Supply: ' + tooltipItem.parsed.x;
                        } else if (tooltipItem.datasetIndex === 2) {
                            return 'Net Difference: ' + tooltipItem.parsed.x;
                        }
                    }
                }
            },
            legend: {
                display: false
            }
        },
        interaction: {
            mode: 'index',
            axis: 'y'
        },
        responsive: false,
        aspectRatio: 1,
        scales: {
            x: {
                position: "top"
            }
        }
    };

    // Build the chart properties dynamically
    labourOccupationValuesRef.current.sort((a, b) => a.Difference - b.Difference);
    // const groups = [...new Set(labour_occupation_test_data.map(item => item.Lvl1group))];
    const occupations = [...new Set(labourOccupationValuesRef.current.map(item => item.ResourceGroup))];

    // Set the chart labels and dataset
    const labour_occupation_chart = {
        labels: occupations,
        datasets: [
            {
                type: 'line',
                data: labourOccupationValuesRef.current.map(item => item.Demand),
                backgroundColor: labour_colours.find(x => x.labour === 'demand')?.colour,
                borderColor: 'rgba(0, 0, 0, 0)',
                pointRadius: 4
            },
            {
                type: 'line',
                data: labourOccupationValuesRef.current.map(item => item.Supply),
                backgroundColor: labour_colours.find(x => x.labour === 'supply')?.colour,
                borderColor: 'rgba(0, 0, 0, 0)',
                pointRadius: 4
            },
            {
                type: 'bar',
                data: labourOccupationValuesRef.current.map(item => item.Difference),
                backgroundColor: labour_colours.find(x => x.labour === 'difference')?.colour
            }
        ]
    };
    // End - labour occupation chart

    useEffect(() => {
        if (labourSupplyDemandStatus == 'error') {
            console.log(labourSupplyDemandError);
        } else if (labourSupplyDemandStatus == 'success' && labourSupplyDemandFetchStatus == 'idle') {
            labourSupplyDemandValuesRef.current = labourSupplyDemandValues.executeRetrieveLabourSupplyDemand;
            setCompleted(prev => ++prev);
        }
    }, [labourSupplyDemandValues, labourSupplyDemandStatus, labourSupplyDemandFetchStatus, labourSupplyDemandError]);

    useEffect(() => {
        if (labourOccupationStatus == 'error') {
            console.log(labourOccupationError);
        } else if (labourOccupationStatus == 'success' && labourOccupationFetchStatus == 'idle') {
            labourOccupationValuesRef.current = labourOccuptionValues.executeRetrieveLabourOccupation;
            setCompleted(prev => ++prev);
        }
    }, [labourOccuptionValues, labourOccupationStatus, labourOccupationFetchStatus, labourOccupationError]);

    useEffect(() => {
        if (updateLabourCharts) {
            labourChartLoader.current!.style.display = 'flex';
            // The code related to setting the month/year
            // is a bit convoluted due to the fiscal year 
            // slider and the reset filter function.
            if (queryVariables.inStartYear !== 0 && queryVariables.inEndYear !== 0) {
                const date = new Date(`${month.inYear}-${month.inMonth}`);
                const startDate = new Date(`${queryVariables.inStartYear - 1}-07`);
                const endDate = new Date(`${queryVariables.inEndYear}-06`)
                if (date < startDate || date > endDate) {
                    const midDate = new Date((startDate.getTime() + endDate.getTime()) / 2);
                    queryVariables.inMonth = midDate.getMonth() + 1;
                    queryVariables.inYear = midDate.getFullYear();
                }
            }
            setMonth({
                inMonth: queryVariables.inMonth,
                inYear: queryVariables.inYear
            });
            refetchLabourSupplyDemandValues();
            refetchLabourOccupationValues();
        }
    }, [updateLabourCharts]);

    useEffect(() => {
        if (completed >= 2) {
            setCompleted(0);
            labourChartLoader.current!.style.display = 'none';
            setLabourTab(false);
        }
    }, [completed]);

    return (<Stack>
        <Box ref={labourChartLoader} style={{ position: "absolute", display: "flex", zIndex: "200", width: 1000, height: 1270, justifyContent: "center", alignItems: "center", backgroundColor: "rgba(51, 51, 51, 0.4)" }}>
            <Oval
                visible={true}
                height="80"
                width="80"
                color="#005670"
                secondaryColor="#005670"
                ariaLabel="oval-loading"
                wrapperStyle={{}}
                wrapperClass=""
            />
        </Box>
        <Grid container direction="column" spacing={1}>
            <Grid item>
                <Box sx={{ width: 1000, fontStyle: "italic", fontWeight: 700, mt: 2, fontFamily: "ProximaNova-Bold", paddingBottom: "5px", margin: 0 }}>
                    <strong>{labourCaveat}</strong>
                </Box>
                <Box style={{ display: "flex", width: 1000, backgroundColor: "rgb(0, 86, 112)" }}>
                    <Box style={{ display: "flex", width: 600, padding: 2, color: "#FFF", fontWeight: 600, fontSize: "1.25rem", fontFamily: "ProximaNova", justifyContent: "flex-start", alignItems: "center" }}>Supply vs demand over time (FTE per month)
                    </Box>
                    <Grid container spacing={1} columns={2} sx={{ width: 400, flexGrow: 1, justifyContent: "flex-end", backgroundColor: "white" }}>
                        <MapPlotFilterList
                            AllMapLists={allMapLists}
                            plotQueryProps={labourPlotQueryProps}
                            setPlotQueryProps={setLabourPlotQueryProps}
                            filterConfigList={labourShortageFiltersConfigList}
                            listsDisabled={false}
                            applyUpdates={applyUpdates} />
                    </Grid>
                </Box>
                <Box style={{ display: "relative", width: 1000, height: 550 }}>
                    <Chart options={labour_monthly_options} data={labour_value_chart} width="1000" height="550" />
                </Box>
            </Grid>
            <Grid item>
                <Box style={{ display: "flex", width: 1000, backgroundColor: "rgb(0, 86, 112)" }}>
                    <Box style={{ display: "flex", width: 800, color: "#FFF", fontWeight: 600, fontSize: "1.25rem", fontFamily: "ProximaNova", justifyContent: "flex-start", alignItems: "center", }}>Supply vs demand by occupation (FTE per month)
                    </Box>
                    <Grid container spacing={1} columns={1} sx={{ width: 200, flexGrow: 1, justifyContent: "flex-end", backgroundColor: "white" }}>
                        <Grid item xs={1}>
                            <FormControl variant="filled" sx={{ width: "100%" }}>
                                <InputLabel id="demo-simple-select-filled-label">Month</InputLabel>
                                <Select
                                    label="Month"
                                    name="inMonth"
                                    fullWidth
                                    value={`${month.inYear}-${month.inMonth >= 10 ? month.inMonth : '0' + month.inMonth}-01T00:00:00.000Z`}
                                    variant="filled"
                                    size="small"
                                    disabled={false}
                                    onChange={(e) => {
                                        const date = new Date(e.target.value);
                                        queryVariables.inMonth = date.getMonth() + 1;
                                        queryVariables.inYear = date.getFullYear();
                                        setMonth({
                                            inMonth: queryVariables.inMonth,
                                            inYear: queryVariables.inYear
                                        });
                                        refetchLabourOccupationValues();
                                    }}
                                >
                                    {labourSupplyDemandValuesRef.current.map((item) => (
                                        <MenuItem key={item.ObservationDate} value={item.ObservationDate}>
                                            {new Date(item.ObservationDate).toLocaleDateString("en-AU", { year: "numeric", month: "long" })}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
                <Box style={{ display: "relative", width: 1000, height: 550, overflow: "auto" }}>
                    <Chart options={labour_occupation_options} data={labour_occupation_chart} height="1000" width="980" />
                </Box>
            </Grid>
        </Grid>
    </Stack>)
}